import { slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { FormField, Box, Label, FormHelp, Input, FieldSet, LabeledControl, Radio } from '@zillow/constellation';
import React, { forwardRef, useRef, useState, useImperativeHandle, useEffect, useCallback } from 'react';

var UncontrolledInputField = /*#__PURE__*/forwardRef(function UncontrolledInputField(props, ref) {
  var inputRef = useRef(null);
  var _useState = useState(function () {
      return null;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    error = _useState2[0],
    setError = _useState2[1];
  useImperativeHandle(ref, function () {
    return {
      updateAndGetError: function updateAndGetError() {
        if (!inputRef.current) {
          throw new Error('Input ref not available');
        }
        var nextError = props.getError(inputRef.current.value);
        setError(nextError || null);
        return nextError;
      },
      setError: setError,
      setValue: function setValue(value) {
        if (!inputRef.current) {
          throw new Error('Input ref not available');
        }
        inputRef.current.value = value;
      },
      focus: function focus() {
        if (!inputRef.current) {
          throw new Error('Input ref not available');
        }
        inputRef.current.focus();
        var focusInterval;
        var focusAndCheck = function focusAndCheck() {
          if (!inputRef.current) {
            throw new Error('Input ref not available');
          }
          inputRef.current.focus();
          if (inputRef.current === document.activeElement) {
            window.clearInterval(focusInterval);
            return true;
          }
          return false;
        };
        // repeat until its focused
        if (!focusAndCheck()) {
          focusInterval = window.setInterval(focusAndCheck, 10);
        }
      }
    };
  });
  useEffect(function () {
    var _props$onMount;
    (_props$onMount = props.onMount) === null || _props$onMount === void 0 || _props$onMount.call(props, inputRef);
  }, []);
  var valueOnFocus = useRef('');
  return /*#__PURE__*/React.createElement(FormField, _extends({}, props.formFieldProps, {
    label: /*#__PURE__*/React.createElement(Box, {
      as: Label,
      sx: {
        '&&&&': {
          display: 'none'
        }
      }
    }, props.placeholder),
    description: /*#__PURE__*/React.createElement(FormHelp, {
      error: !!error
    }, error || /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0")),
    control: /*#__PURE__*/React.createElement(Input, {
      autoComplete: props.autoComplete,
      autoFocus: props.autoFocus,
      defaultValue: props.defaultValue,
      error: error,
      name: props.name,
      onFocus: function onFocus() {
        valueOnFocus.current = inputRef.current.value;
      },
      onBlur: function onBlur() {
        setErrorIfNotFocused(setError, props.getError);
        if (inputRef.current.value !== valueOnFocus.current) {
          var _props$onBlurAfterCha;
          (_props$onBlurAfterCha = props.onBlurAfterChange) === null || _props$onBlurAfterCha === void 0 || _props$onBlurAfterCha.call(props, inputRef.current.value);
        }
      },
      onChange: function onChange(e) {
        var _props$onChange;
        (_props$onChange = props.onChange) === null || _props$onChange === void 0 || _props$onChange.call(props, e.target.value);
        setErrorIfNotFocused(setError, props.getError)(e);
      },
      placeholder: props.placeholder,
      ref: inputRef,
      type: props.type
    })
  }));
});
var UncontrolledFieldSet = /*#__PURE__*/forwardRef(function UncontrolledFieldSet(props, ref) {
  var inputRefs = useRef([]);
  var _useState3 = useState(function () {
      return null;
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  useImperativeHandle(ref, function () {
    return {
      updateAndGetError: function updateAndGetError() {
        var _inputRefs$current$fi;
        var value = (_inputRefs$current$fi = inputRefs.current.find(function (ref) {
          return ref.checked;
        })) === null || _inputRefs$current$fi === void 0 ? void 0 : _inputRefs$current$fi.value;
        var nextError = props.getError(value);
        setError(nextError || null);
        return nextError;
      },
      setError: setError
    };
  });

  // Needs to be stable otherwise ref fires too much
  var addInputRef = useCallback(function (el) {
    if (!el) {
      inputRefs.current = inputRefs.current.filter(function (ref) {
        return ref !== el;
      });
      return;
    }
    if (!inputRefs.current.includes(el)) {
      inputRefs.current.push(el);
    }
  }, []);
  return /*#__PURE__*/React.createElement(FieldSet, {
    description: /*#__PURE__*/React.createElement(FormHelp, {
      error: !!error
    }, error || /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0")),
    onChange: function onChange(e) {
      return setError(props.getError(e.target.value) || null);
    }
  }, props.options.map(function (option) {
    return /*#__PURE__*/React.createElement(LabeledControl, {
      disabled: props.disabled,
      key: option.value,
      label: /*#__PURE__*/React.createElement(Label, null, option.label),
      control: /*#__PURE__*/React.createElement(Radio, {
        name: props.name,
        value: option.value,
        onChange: function onChange(e) {
          var _props$onChange2;
          return props === null || props === void 0 || (_props$onChange2 = props.onChange) === null || _props$onChange2 === void 0 ? void 0 : _props$onChange2.call(props, e.target.value);
        },
        ref: addInputRef
      })
    });
  }));
});
function setErrorIfNotFocused(setError, getError) {
  return function (e) {
    if (document.activeElement === e.target) {
      setError(null);
      return;
    }
    setError(getError(e.target.value) || null);
  };
}

export { UncontrolledFieldSet, UncontrolledInputField };
